import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../../components/MainLayout';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { fetchModelPerformanceSummary, fetchTrainingHistory } from '../../services/api';
import { useTranslation } from '../../context/LanguageContext';
import './ModelDashboard.css';

function ModelDashboard() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [activeTab, setActiveTab] = useState('lstm');
  const [selectedModel, setSelectedModel] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [dataSource, setDataSource] = useState(null); // 'gpu' or 'mock'
  const [showSummaryModal, setShowSummaryModal] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchModelPerformanceSummary();
      setDataSource(data._source || 'unknown');
      setSummaryData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleModelClick = async (model) => {
    if (selectedModel?.model_id === model.model_id) {
      setSelectedModel(null);
      setHistoryData(null);
      return;
    }
    setSelectedModel(model);
    setHistoryLoading(true);
    try {
      const history = await fetchTrainingHistory(model.model_id, activeTab === 'lstm' ? 'lstm' : 'ae');
      setHistoryData(history);
    } catch (err) {
      console.error('Failed to load training history:', err);
      setHistoryData(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  const currentModels = summaryData?.[activeTab]?.models || [];

  const chartData = historyData ? historyData.epochs?.map((epoch, i) => ({
    epoch,
    train_loss: historyData.train_loss?.[i],
    val_loss: historyData.val_loss?.[i]
  })) : [];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">Epoch {label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {`${entry.name}: ${entry.value?.toFixed(6)}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <MainLayout>
      <div className="model-dashboard">
        {/* 헤더 */}
        <div className="model-header">
          <div className="model-header-left">
            <h2 className="model-title">{t('pages.modelDashboard.title')}</h2>
            <p className="model-subtitle">{t('pages.modelDashboard.subtitle')}</p>
          </div>
          <div className="model-header-right">
            {dataSource && (
              <span className={`data-source-badge ${dataSource}`}>
                <span className="material-symbols-outlined">{dataSource === 'gpu' ? 'cloud_done' : 'cloud_off'}</span>
                {dataSource === 'gpu' ? t('pages.modelDashboard.gpuLive') : t('pages.modelDashboard.mockData')}
              </span>
            )}
            <button className="summary-btn" onClick={() => setShowSummaryModal(true)} disabled={loading || !summaryData}>
              <span className="material-symbols-outlined">summarize</span>
              {t('pages.modelDashboard.summaryBtn')}
            </button>
            <button className="model-refresh-btn" onClick={loadData} disabled={loading}>
              <span className="material-symbols-outlined">refresh</span>
              {t('pages.modelDashboard.refresh')}
            </button>
          </div>
        </div>

        {error && (
          <div className="model-error">
            <span className="material-symbols-outlined">error</span>
            {t('pages.modelDashboard.errorPrefix', { error })}
          </div>
        )}

        {loading ? (
          <div className="model-loading">{t('pages.modelDashboard.loading')}</div>
        ) : summaryData && (
          <>
            {/* KPI 카드 */}
            <div className="kpi-grid">
              <div className="kpi-card">
                <div className="kpi-content">
                  <div className="kpi-info">
                    <div className="kpi-label">{t('pages.modelDashboard.kpi.lstm')}</div>
                    <div className="kpi-value blue">{summaryData.lstm?.total_count || 0}</div>
                    <div className="kpi-unit">{t('pages.modelDashboard.kpi.unitCount')}</div>
                  </div>
                  <div className="kpi-icon blue">
                    <span className="material-symbols-outlined">neurology</span>
                  </div>
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-content">
                  <div className="kpi-info">
                    <div className="kpi-label">{t('pages.modelDashboard.kpi.ae')}</div>
                    <div className="kpi-value purple">{summaryData.ae?.total_count || 0}</div>
                    <div className="kpi-unit">{t('pages.modelDashboard.kpi.unitCount')}</div>
                  </div>
                  <div className="kpi-icon purple">
                    <span className="material-symbols-outlined">hub</span>
                  </div>
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-content">
                  <div className="kpi-info">
                    <div className="kpi-label">{t('pages.modelDashboard.kpi.total')}</div>
                    <div className="kpi-value green">{(summaryData.lstm?.total_count || 0) + (summaryData.ae?.total_count || 0)}</div>
                    <div className="kpi-unit">{t('pages.modelDashboard.kpi.unitCount')}</div>
                  </div>
                  <div className="kpi-icon green">
                    <span className="material-symbols-outlined">analytics</span>
                  </div>
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-content">
                  <div className="kpi-info">
                    <div className="kpi-label">{t('pages.modelDashboard.kpi.totalData')}</div>
                    <div className="kpi-value yellow">{(currentModels.reduce((sum, m) => sum + (m.data_size || 0), 0)).toLocaleString()}</div>
                    <div className="kpi-unit">{t('pages.modelDashboard.kpi.unitRecords')}</div>
                  </div>
                  <div className="kpi-icon yellow">
                    <span className="material-symbols-outlined">database</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 메인 콘텐츠 */}
            <div className="model-main-content">
              {/* 탭 + 통계 */}
              <div className="model-controls-row">
                <div className="model-tab-bar">
                  <button
                    className={`model-tab ${activeTab === 'lstm' ? 'active' : ''}`}
                    onClick={() => { setActiveTab('lstm'); setSelectedModel(null); setHistoryData(null); }}
                  >
                    LSTM Attention ({summaryData.lstm?.total_count || 0})
                  </button>
                  <button
                    className={`model-tab ${activeTab === 'ae' ? 'active' : ''}`}
                    onClick={() => { setActiveTab('ae'); setSelectedModel(null); setHistoryData(null); }}
                  >
                    AutoEncoder ({summaryData.ae?.total_count || 0})
                  </button>
                </div>
              </div>

              {/* 테이블 + 차트 */}
              <div className={`model-body ${selectedModel ? '' : 'no-detail'}`}>
                {/* 모델 테이블 */}
                <div className="model-table-container">
                  <div className="model-table-scroll">
                    <table className="model-table">
                      <thead>
                        <tr>
                          <th>{t('pages.modelDashboard.table.modelId')}</th>
                          <th>{t('pages.modelDashboard.table.equipment')}</th>
                          <th>{t('pages.modelDashboard.table.mold')}</th>
                          <th>{t('pages.modelDashboard.table.valLoss')}</th>
                          <th>{t('pages.modelDashboard.table.accuracy')}</th>
                          <th>{t('pages.modelDashboard.table.precision')}</th>
                          <th>{t('pages.modelDashboard.table.recall')}</th>
                          <th>{t('pages.modelDashboard.table.f1')}</th>
                          <th>{t('pages.modelDashboard.table.fpr')}</th>
                          <th>{t('pages.modelDashboard.table.dataCount')}</th>
                          <th>{t('pages.modelDashboard.table.trainedAt')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentModels.length === 0 ? (
                          <tr><td colSpan="11" className="model-table-empty">{t('pages.modelDashboard.table.empty')}</td></tr>
                        ) : currentModels.map((model, idx) => {
                          const cm = model.confusion_matrix;
                          return (
                            <tr
                              key={model.model_id || idx}
                              className={selectedModel?.model_id === model.model_id ? 'selected' : ''}
                              onClick={() => handleModelClick(model)}
                            >
                              <td className="model-id-cell">{model.model_id}</td>
                              <td>{model.equipment || '-'}</td>
                              <td>{model.craft_id || '-'}</td>
                              <td className="model-val-loss">
                                {model.best_val_loss != null ? model.best_val_loss.toFixed(6) : '-'}
                              </td>
                              <td className={`model-accuracy ${cm?.accuracy >= 90 ? 'good' : cm?.accuracy >= 80 ? 'warn' : 'bad'}`}>
                                {cm?.accuracy != null ? `${cm.accuracy}%` : '-'}
                              </td>
                              <td className={`model-accuracy ${cm?.precision >= 90 ? 'good' : cm?.precision >= 80 ? 'warn' : 'bad'}`}>
                                {cm?.precision != null ? `${cm.precision}%` : '-'}
                              </td>
                              <td className={`model-accuracy ${cm?.recall >= 90 ? 'good' : cm?.recall >= 80 ? 'warn' : 'bad'}`}>
                                {cm?.recall != null ? `${cm.recall}%` : '-'}
                              </td>
                              <td className={`model-accuracy ${cm?.f1_score >= 90 ? 'good' : cm?.f1_score >= 80 ? 'warn' : 'bad'}`}>
                                {cm?.f1_score != null ? `${cm.f1_score}%` : '-'}
                              </td>
                              <td className={`model-fpr ${cm?.FPR <= 5 ? 'good' : cm?.FPR <= 10 ? 'warn' : 'bad'}`}>
                                {cm?.FPR != null ? `${cm.FPR}%` : '-'}
                              </td>
                              <td>{model.data_size?.toLocaleString() || '-'}</td>
                              <td>{model.trained_at ? new Date(model.trained_at).toLocaleDateString('ko-KR') : '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* 학습 곡선 차트 */}
                {selectedModel && (
                  <div className="model-detail-panel">
                    <div className="model-detail-header">
                      <h3>
                        <span className="material-symbols-outlined">show_chart</span>
                        {selectedModel.model_id}
                      </h3>
                      <button className="model-detail-close" onClick={() => { setSelectedModel(null); setHistoryData(null); }}>
                        <span className="material-symbols-outlined">close</span>
                      </button>
                    </div>

                    <div className="model-detail-scroll">
                      {/* 혼동행렬 */}
                      {selectedModel.confusion_matrix && (
                        <div className="confusion-matrix-section">
                          <h4 className="section-title">
                            <span className="material-symbols-outlined">grid_on</span>
                            {t('pages.modelDashboard.detail.confusionMatrix')}
                          </h4>
                          <div className="cm-content">
                            <div className="cm-grid">
                              <div className="cm-header-row">
                                <div className="cm-corner"></div>
                                <div className="cm-col-header">{t('pages.modelDashboard.detail.predNormal')}</div>
                                <div className="cm-col-header">{t('pages.modelDashboard.detail.predAnomaly')}</div>
                              </div>
                              <div className="cm-data-row">
                                <div className="cm-row-header">{t('pages.modelDashboard.detail.actualNormal')}</div>
                                <div className="cm-cell cm-tn">
                                  <div className="cm-cell-value">{selectedModel.confusion_matrix.TN.toLocaleString()}</div>
                                  <div className="cm-cell-label">{t('pages.modelDashboard.detail.tn')}</div>
                                </div>
                                <div className="cm-cell cm-fp">
                                  <div className="cm-cell-value">{selectedModel.confusion_matrix.FP.toLocaleString()}</div>
                                  <div className="cm-cell-label">{t('pages.modelDashboard.detail.fp')}</div>
                                </div>
                              </div>
                              <div className="cm-data-row">
                                <div className="cm-row-header">{t('pages.modelDashboard.detail.actualAnomaly')}</div>
                                <div className="cm-cell cm-fn">
                                  <div className="cm-cell-value">{selectedModel.confusion_matrix.FN}</div>
                                  <div className="cm-cell-label">{t('pages.modelDashboard.detail.fn')}</div>
                                </div>
                                <div className="cm-cell cm-tp">
                                  <div className="cm-cell-value">{selectedModel.confusion_matrix.TP}</div>
                                  <div className="cm-cell-label">{t('pages.modelDashboard.detail.tp')}</div>
                                </div>
                              </div>
                            </div>
                            <div className="cm-metrics">
                              <div className="cm-metric">
                                <span className="cm-metric-label">{t('pages.modelDashboard.detail.validSample')}</span>
                                <span className="cm-metric-value">{t('pages.modelDashboard.detail.validSampleSuffix', { n: selectedModel.confusion_matrix.total_samples.toLocaleString() })}</span>
                              </div>
                              <div className="cm-metric">
                                <span className="cm-metric-label">{t('pages.modelDashboard.detail.accuracyLabel')}</span>
                                <span className={`cm-metric-value ${selectedModel.confusion_matrix.accuracy >= 90 ? 'good' : 'warn'}`}>
                                  {selectedModel.confusion_matrix.accuracy}%
                                </span>
                              </div>
                              <div className="cm-metric">
                                <span className="cm-metric-label">Precision</span>
                                <span className={`cm-metric-value ${selectedModel.confusion_matrix.precision >= 90 ? 'good' : selectedModel.confusion_matrix.precision >= 80 ? 'warn' : 'bad'}`}>
                                  {selectedModel.confusion_matrix.precision != null ? `${selectedModel.confusion_matrix.precision}%` : '-'}
                                </span>
                              </div>
                              <div className="cm-metric">
                                <span className="cm-metric-label">Recall</span>
                                <span className={`cm-metric-value ${selectedModel.confusion_matrix.recall >= 90 ? 'good' : selectedModel.confusion_matrix.recall >= 80 ? 'warn' : 'bad'}`}>
                                  {selectedModel.confusion_matrix.recall != null ? `${selectedModel.confusion_matrix.recall}%` : '-'}
                                </span>
                              </div>
                              <div className="cm-metric">
                                <span className="cm-metric-label">F1 Score</span>
                                <span className={`cm-metric-value ${selectedModel.confusion_matrix.f1_score >= 90 ? 'good' : selectedModel.confusion_matrix.f1_score >= 80 ? 'warn' : 'bad'}`}>
                                  {selectedModel.confusion_matrix.f1_score != null ? `${selectedModel.confusion_matrix.f1_score}%` : '-'}
                                </span>
                              </div>
                              <div className="cm-metric">
                                <span className="cm-metric-label">{t('pages.modelDashboard.detail.fprLabel')}</span>
                                <span className={`cm-metric-value ${selectedModel.confusion_matrix.FPR <= 5 ? 'good' : 'warn'}`}>
                                  {selectedModel.confusion_matrix.FPR}%
                                </span>
                              </div>
                              <div className="cm-metric">
                                <span className="cm-metric-label">{t('pages.modelDashboard.detail.trainThreshold')}</span>
                                <span className="cm-metric-value">{selectedModel.confusion_matrix.threshold_used?.toFixed(6)}</span>
                              </div>
                              <div className="cm-metric">
                                <span className="cm-metric-label">{t('pages.modelDashboard.detail.valThreshold')}</span>
                                <span className="cm-metric-value">{selectedModel.confusion_matrix.val_threshold?.toFixed(6) || '-'}</span>
                              </div>
                            </div>
                            <div className="cm-note">
                              {t('pages.modelDashboard.detail.cmNote')}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* 학습 곡선 차트 */}
                      <div className="training-curve-section">
                        <h4 className="section-title">
                          <span className="material-symbols-outlined">timeline</span>
                          {t('pages.modelDashboard.detail.trainingCurve')}
                        </h4>
                        {historyLoading ? (
                          <div className="model-loading">{t('pages.modelDashboard.historyLoading')}</div>
                        ) : chartData.length > 0 ? (
                          <div className="model-chart-wrapper">
                            <div className="model-chart-area">
                              <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData} margin={{ top: 10, right: 20, left: -10, bottom: 5 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                                  <XAxis
                                    dataKey="epoch"
                                    stroke="#9CA3AF"
                                    tick={{ fill: '#9CA3AF', fontSize: 11 }}
                                    label={{ value: 'Epoch', position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 11 }}
                                  />
                                  <YAxis
                                    stroke="#9CA3AF"
                                    tick={{ fill: '#9CA3AF', fontSize: 11 }}
                                    label={{ value: 'Loss', angle: -90, position: 'insideLeft', fill: '#9CA3AF', fontSize: 11 }}
                                  />
                                  <Tooltip content={<CustomTooltip />} />
                                  <Legend
                                    verticalAlign="top"
                                    align="right"
                                    wrapperStyle={{ paddingBottom: '5px' }}
                                    formatter={(value) => <span style={{ color: '#9CA3AF', fontSize: '12px' }}>{value}</span>}
                                  />
                                  <Line type="monotone" dataKey="train_loss" stroke="#3B82F6" name="Train Loss" strokeWidth={2} dot={false} />
                                  <Line type="monotone" dataKey="val_loss" stroke="#F59E0B" name="Val Loss" strokeWidth={2} dot={false} />
                                </LineChart>
                              </ResponsiveContainer>
                            </div>
                            <div className="model-chart-info">
                              <span>{t('pages.modelDashboard.detail.totalEpoch', { n: historyData.total_epochs })}</span>
                              <span>{t('pages.modelDashboard.detail.convergence', { n: historyData.convergence_epoch || '-' })}</span>
                              <span>{t('pages.modelDashboard.detail.finalValLoss', { n: historyData.best_val_loss?.toFixed(6) || '-' })}</span>
                              <span>{t('pages.modelDashboard.detail.reduction', { n: historyData.improvement?.train_loss_reduction?.toFixed(1) || '-' })}</span>
                            </div>
                          </div>
                        ) : (
                          <div className="model-no-history">{t('pages.modelDashboard.noHistory')}</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
        {/* 성능요약 모달 */}
        {showSummaryModal && summaryData && (() => {
          const calcMetrics = (models) => {
            const accs = models.map(m => m.confusion_matrix?.accuracy).filter(v => v != null);
            const precs = models.map(m => m.confusion_matrix?.precision).filter(v => v != null);
            const recs = models.map(m => m.confusion_matrix?.recall).filter(v => v != null);
            const f1s = models.map(m => m.confusion_matrix?.f1_score).filter(v => v != null);
            const avg = arr => arr.length > 0 ? (arr.reduce((s, v) => s + v, 0) / arr.length).toFixed(2) : '-';
            const totalData = models.reduce((s, m) => s + (m.data_size || 0), 0);
            return {
              count: models.length,
              totalData,
              accuracy: avg(accs),
              precision: avg(precs),
              recall: avg(recs),
              f1: avg(f1s),
            };
          };
          const lstmModels = summaryData.lstm?.models || [];
          const aeModels = summaryData.ae?.models || [];
          const lstm = calcMetrics(lstmModels);
          const ae = calcMetrics(aeModels);
          const totalModels = lstm.count + ae.count;
          const totalData = lstm.totalData + ae.totalData;

          return (
            <div className="summary-modal-overlay" onClick={() => setShowSummaryModal(false)}>
              <div className="summary-modal" onClick={e => e.stopPropagation()}>
                <div className="summary-modal-header">
                  <h2>{t('pages.modelDashboard.summary.title')}</h2>
                  <p className="summary-modal-subtitle">{t('pages.modelDashboard.summary.subtitle')}</p>
                  <button className="summary-modal-close" onClick={() => setShowSummaryModal(false)}>
                    <span className="material-symbols-outlined">close</span>
                  </button>
                </div>

                <div className="summary-modal-body">
                  {/* 총괄 요약 */}
                  <div className="summary-overview">
                    <div className="summary-overview-item">
                      <span className="summary-overview-label">{t('pages.modelDashboard.summary.totalModels')}</span>
                      <span className="summary-overview-value blue">{totalModels}</span>
                      <span className="summary-overview-unit">{t('pages.modelDashboard.summary.unitCount')}</span>
                    </div>
                    <div className="summary-overview-divider" />
                    <div className="summary-overview-item">
                      <span className="summary-overview-label">{t('pages.modelDashboard.summary.totalData')}</span>
                      <span className="summary-overview-value green">{totalData.toLocaleString()}</span>
                      <span className="summary-overview-unit">{t('pages.modelDashboard.summary.unitRecords')}</span>
                    </div>
                    <div className="summary-overview-divider" />
                    <div className="summary-overview-item">
                      <span className="summary-overview-label">{t('pages.modelDashboard.summary.lstmCount')}</span>
                      <span className="summary-overview-value cyan">{lstm.count}</span>
                      <span className="summary-overview-unit">{t('pages.modelDashboard.summary.unitCount')}</span>
                    </div>
                    <div className="summary-overview-divider" />
                    <div className="summary-overview-item">
                      <span className="summary-overview-label">{t('pages.modelDashboard.summary.aeCount')}</span>
                      <span className="summary-overview-value purple">{ae.count}</span>
                      <span className="summary-overview-unit">{t('pages.modelDashboard.summary.unitCount')}</span>
                    </div>
                  </div>

                  {/* 모델별 성능 비교 */}
                  <div className="summary-comparison">
                    {/* LSTM */}
                    <div className="summary-model-card lstm">
                      <div className="summary-card-header">
                        <span className="material-symbols-outlined">neurology</span>
                        <h3>LSTM Attention</h3>
                        <span className="summary-card-count">{t('pages.modelDashboard.summary.modelCount', { n: lstm.count })}</span>
                      </div>
                      <div className="summary-card-body">
                        <div className="summary-metric-row">
                          <span className="summary-metric-name">{t('pages.modelDashboard.summary.avgAccuracy')}</span>
                          <span className={`summary-metric-val ${parseFloat(lstm.accuracy) >= 90 ? 'good' : parseFloat(lstm.accuracy) >= 80 ? 'warn' : 'bad'}`}>{lstm.accuracy}%</span>
                        </div>
                        <div className="summary-metric-row">
                          <span className="summary-metric-name">{t('pages.modelDashboard.summary.avgPrecision')}</span>
                          <span className={`summary-metric-val ${parseFloat(lstm.precision) >= 90 ? 'good' : parseFloat(lstm.precision) >= 80 ? 'warn' : 'bad'}`}>{lstm.precision}%</span>
                        </div>
                        <div className="summary-metric-row">
                          <span className="summary-metric-name">{t('pages.modelDashboard.summary.avgRecall')}</span>
                          <span className={`summary-metric-val ${parseFloat(lstm.recall) >= 90 ? 'good' : parseFloat(lstm.recall) >= 80 ? 'warn' : 'bad'}`}>{lstm.recall}%</span>
                        </div>
                        <div className="summary-metric-row highlight">
                          <span className="summary-metric-name">{t('pages.modelDashboard.summary.avgF1')}</span>
                          <span className={`summary-metric-val ${parseFloat(lstm.f1) >= 90 ? 'good' : parseFloat(lstm.f1) >= 80 ? 'warn' : 'bad'}`}>{lstm.f1}%</span>
                        </div>
                        <div className="summary-metric-row sub">
                          <span className="summary-metric-name">{t('pages.modelDashboard.summary.trainData')}</span>
                          <span className="summary-metric-val">{lstm.totalData.toLocaleString()}{t('pages.modelDashboard.summary.unitRecords')}</span>
                        </div>
                      </div>
                    </div>

                    {/* AE */}
                    <div className="summary-model-card ae">
                      <div className="summary-card-header">
                        <span className="material-symbols-outlined">hub</span>
                        <h3>AutoEncoder</h3>
                        <span className="summary-card-count">{t('pages.modelDashboard.summary.modelCount', { n: ae.count })}</span>
                      </div>
                      <div className="summary-card-body">
                        {ae.precision === '-' ? (
                          <>
                            <div className="summary-metric-row">
                              <span className="summary-metric-name">{t('pages.modelDashboard.summary.avgAccuracy')}</span>
                              <span className={`summary-metric-val ${parseFloat(ae.accuracy) >= 90 ? 'good' : parseFloat(ae.accuracy) >= 80 ? 'warn' : 'bad'}`}>{ae.accuracy}%</span>
                            </div>
                            <div className="summary-metric-row">
                              <span className="summary-metric-name">{t('pages.modelDashboard.summary.avgPrecision')}</span>
                              <span className="summary-metric-val dim">{t('pages.modelDashboard.summary.retrainNeeded')}</span>
                            </div>
                            <div className="summary-metric-row">
                              <span className="summary-metric-name">{t('pages.modelDashboard.summary.avgRecall')}</span>
                              <span className="summary-metric-val dim">{t('pages.modelDashboard.summary.retrainNeeded')}</span>
                            </div>
                            <div className="summary-metric-row highlight">
                              <span className="summary-metric-name">{t('pages.modelDashboard.summary.avgF1')}</span>
                              <span className="summary-metric-val dim">{t('pages.modelDashboard.summary.retrainNeeded')}</span>
                            </div>
                            <div className="summary-metric-row sub">
                              <span className="summary-metric-name">{t('pages.modelDashboard.summary.trainData')}</span>
                              <span className="summary-metric-val">{ae.totalData.toLocaleString()}{t('pages.modelDashboard.summary.unitRecords')}</span>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="summary-metric-row">
                              <span className="summary-metric-name">{t('pages.modelDashboard.summary.avgAccuracy')}</span>
                              <span className={`summary-metric-val ${parseFloat(ae.accuracy) >= 90 ? 'good' : parseFloat(ae.accuracy) >= 80 ? 'warn' : 'bad'}`}>{ae.accuracy}%</span>
                            </div>
                            <div className="summary-metric-row">
                              <span className="summary-metric-name">{t('pages.modelDashboard.summary.avgPrecision')}</span>
                              <span className={`summary-metric-val ${parseFloat(ae.precision) >= 90 ? 'good' : parseFloat(ae.precision) >= 80 ? 'warn' : 'bad'}`}>{ae.precision}%</span>
                            </div>
                            <div className="summary-metric-row">
                              <span className="summary-metric-name">{t('pages.modelDashboard.summary.avgRecall')}</span>
                              <span className={`summary-metric-val ${parseFloat(ae.recall) >= 90 ? 'good' : parseFloat(ae.recall) >= 80 ? 'warn' : 'bad'}`}>{ae.recall}%</span>
                            </div>
                            <div className="summary-metric-row highlight">
                              <span className="summary-metric-name">{t('pages.modelDashboard.summary.avgF1')}</span>
                              <span className={`summary-metric-val ${parseFloat(ae.f1) >= 90 ? 'good' : parseFloat(ae.f1) >= 80 ? 'warn' : 'bad'}`}>{ae.f1}%</span>
                            </div>
                            <div className="summary-metric-row sub">
                              <span className="summary-metric-name">{t('pages.modelDashboard.summary.trainData')}</span>
                              <span className="summary-metric-val">{ae.totalData.toLocaleString()}{t('pages.modelDashboard.summary.unitRecords')}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* 참고사항 */}
                  <div className="summary-footer-note">
                    <span className="material-symbols-outlined">info</span>
                    <span>{t('pages.modelDashboard.summary.footerNote')}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </MainLayout>
  );
}

export default ModelDashboard;
